import { useEffect } from 'react';
import { autreDelaiEfface } from '@nextilt/commun';
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { desabonnerAvantDeSortir } from './abonnementDeCetAppareil';
import { api } from './api/client';
import { lireLaFile, rejouerLaFile, viderLaFile } from './api/fileHorsLigne';
import { useApparence } from './apparence';
import { champsDUnProjetNeuf, creerOuMettreEnFile, OPTIONS_DE_CREATION } from './creation';
import { cles, PREFIXES, relireApres } from './relectures';
import { feuilleAvecLaBulleHabillee, feuilleAvecLeMode, feuilleHabillee } from './habitDeLaFeuille';
import { useAuPouce } from './surface';
import type {
  Bulle,
  ChampsALaNaissance,
  ChangementDHabit,
  ChangementDHabitDUneBulle,
  ChangementDeFiche,
  ChangementDeMode,
  DossierVu,
  Etat,
  Feuille,
  Priorite,
  Regroupement,
  ReponseSession,
} from './api/types';

/**
 * Chargement et mise à jour de l'arbre.
 *
 * La règle qui gouverne ce fichier : **cocher une bulle doit être instantané**, pas attendre
 * le serveur. Chaque écriture pose donc son effet localement d'abord, et
 * remet l'ancien état si le serveur refuse.
 */

/**
 * L'utilisateur connecté, tel que `App` l'a lu à l'ouverture (`GET /api/session`). Null si on
 * ne le sait pas : la file hors ligne ne capture alors rien, plutôt qu'une capture sans
 * propriétaire qui partirait chez le prochain connecté (ticket 01 du chantier comptes).
 */
export function utilisateurConnecte(client: QueryClient): string | null {
  return client.getQueryData<ReponseSession>(cles.session)?.utilisateur ?? null;
}

/**
 * La session telle que le serveur la rend (`GET /api/session`). Une seule définition des
 * options : `App`, la file hors ligne et le cadre la lisent par ici.
 */
export function useSession() {
  return useQuery({ queryKey: cles.session, queryFn: api.session, retry: false, staleTime: 30_000 });
}

/** L'adresse de contact des pages publiques (ticket 05 du chantier comptes), lue sans session. */
export function useContact() {
  return useQuery({ queryKey: cles.contact, queryFn: api.contact, retry: false, staleTime: 60_000 });
}

/** Le même, abonné : ce qui s'affiche suit un changement de session. */
function useUtilisateurConnecte(): string | null {
  return useSession().data?.utilisateur ?? null;
}

/** L'e-mail du compte connecté, pour que le cadre dise avec quel compte on est là (ticket 02). */
export function useEmailConnecte(): string | null {
  return useSession().data?.email ?? null;
}

export function useDossiers() {
  return useQuery({ queryKey: cles.dossiers, queryFn: api.dossiers });
}

export function useFeuille(id: string) {
  return useQuery({ queryKey: cles.feuille(id), queryFn: () => api.feuille(id), enabled: !!id });
}

/**
 * La carte d'une feuille avec des étapes ouvertes sur place (ticket 06 de retours-testeurs).
 *
 * Pendant qu'une nouvelle carte arrive, la précédente de la même feuille reste à l'écran :
 * les bulles glissent de l'une à l'autre au lieu de clignoter. Jamais celle d'une autre
 * feuille : ses places sont centrées sur un autre titre. Tout replié, rien à demander : la
 * feuille porte déjà sa carte repliée.
 */
/** Rien d'ouvert sur place. */
const RIEN_D_OUVERT: readonly string[] = [];

export function useCarteDepliee(
  id: string,
  /** Ce que la carte demande (`demandeDeLaCarte`) ; `null` : la carte de la feuille suffit. */
  demande: { ouvertes: readonly string[]; profondeur: number; importance: Priorite | null } | null
) {
  return useQuery({
    queryKey: cles.carte(
      id,
      demande?.ouvertes ?? RIEN_D_OUVERT,
      demande ? `${demande.profondeur}${demande.importance ? `@${demande.importance}` : ''}` : ''
    ),
    queryFn: () => api.carte(id, demande!.ouvertes, demande!.profondeur, demande!.importance),
    enabled: !!id && demande !== null,
    placeholderData: (avant, requete) => (requete?.queryKey[1] === id ? avant : undefined),
  });
}

/**
 * Regrouper, en Analyse (chantier analyse-au-serveur) : les groupes que le serveur compte, sur les
 * bulles que la carte montre (`ouvertes`). Pendant qu'un regroupement arrive (un autre choix, une
 * étape ouverte ou fermée sur place), le précédent de la même feuille reste : les colonnes ne
 * sautent pas. Il garde le choix qui l'a fait (`par`), pour dire ses noms. `par` nul : rien.
 */
export function useGroupes(id: string | null, par: Regroupement | null, ouvertes: readonly string[]) {
  return useQuery({
    queryKey: cles.groupes(id ?? '', par ?? '', ouvertes),
    queryFn: async () => ({ par: par!, ouvertes, ...(await api.groupes(id!, par!, ouvertes)) }),
    enabled: !!id && par !== null,
    placeholderData: (avant, requete) => (requete?.queryKey[1] === id ? avant : undefined),
  });
}

/**
 * Plusieurs feuilles d'un coup : celles des étapes dépliées dans la vue « Hiérarchie »
 * (ticket 35). Les mêmes clés que `useFeuille` : une étape déjà visitée s'affiche tout de
 * suite, et chaque geste qui relit les feuilles les relit aussi.
 */
export function useFeuilles(ids: string[]) {
  return useQueries({
    queries: ids.map((id) => ({ queryKey: cles.feuille(id), queryFn: () => api.feuille(id) })),
  });
}

/**
 * Créer une bulle dans une feuille.
 *
 * Pas de mise à jour optimiste ici : c'est le serveur qui attribue `ordre`, et poser une
 * bulle fantôme à la mauvaise place ferait bouger la feuille sous les doigts — exactement
 * ce que la §5.4 interdit. La création est rapide ; la fausse place, elle, se voit.
 */
export function useCreer(parentId: string | null, { file = false }: { file?: boolean } = {}) {
  // Un projet créé à l'ordinateur naît comme Paramètres le règle : sa forme, le type et le papier
  // de ses fiches (habit-du-projet 03, types-de-fiche 06).
  const champs = champsDUnProjetNeuf(parentId, useApparence(), useAuPouce());
  return useMutation(optionsDeCreer(useQueryClient(), parentId, file, champs));
}

/**
 * Les options de `useCreer`, sans React. `file` : sans réseau, la bulle attend dans la file et
 * partira au retour du réseau (le champ du bas d'une feuille, seul à y avoir droit avec le + ;
 * spec `telephone`, « Sans réseau ») ; sinon le geste échoue, hors ligne, et l'écran le dit.
 * `champs` : ce avec quoi elle naît (la forme et les fiches d'un projet neuf).
 */
export function optionsDeCreer(
  client: QueryClient,
  parentId: string | null,
  file: boolean,
  champs: ChampsALaNaissance = {}
) {
  return {
    ...OPTIONS_DE_CREATION,
    mutationFn: (titre: string) =>
      creerOuMettreEnFile({ titre, parentId, champs, file }, () => utilisateurConnecte(client)),
    /*
     * Un dossier vit dans `dossiers` et `projets`, et le premier fait quitter à « Maintenant » sa
     * page vide (ticket 14) ; une bulle créée dans une autre change la feuille de sa mère (« 3
     * étapes »), le projet et ce que « Maintenant » propose : `relireApres` relit tout cela.
     */
    onSuccess: () => void relireApres(client, 'creer'),
  };
}

/**
 * Ce qui attend de partir. Affiche dans la feuille pour qu'une bulle capturee hors ligne
 * ne semble jamais perdue. Seulement ce que l'utilisateur connecte a capture.
 */
export function useFileHorsLigne() {
  const utilisateur = useUtilisateurConnecte();
  return useQuery({
    queryKey: cles.fileHorsLigne(utilisateur),
    queryFn: () => lireLaFile(utilisateur),
    // Elle se lit dans l'appareil, sans réseau : c'est justement sans réseau qu'elle doit se
    // relire, sinon react-query la met en pause et la bulle mise en file ne se montre pas.
    networkMode: 'always',
  });
}

/**
 * Rejouer la file des que le reseau revient — et une fois a l'ouverture ou a la connexion,
 * parce qu'on a pu fermer l'app hors ligne et la rouvrir connecte.
 *
 * `utilisateur` est celui qu'`App` a lu ; il ne fait que declencher. Juste avant de rejouer,
 * la session est relue au serveur : c'est son cookie qui portera les creations, et seules les
 * captures de CET utilisateur partent (ticket 01 du chantier comptes). Sans personne de
 * connecte, rien ne part.
 */
export function useRejouerLaFile(utilisateur: string | null) {
  const client = useQueryClient();

  useEffect(() => {
    if (!utilisateur) return;
    const quiEstConnecte = async () => {
      const session = await api.session().catch(() => null);
      return session?.connecte && session.utilisateur === utilisateur ? utilisateur : null;
    };
    const rejouer = async () => {
      const parties = await rejouerLaFile(quiEstConnecte, creerDepuisLaFile);
      if (parties > 0) void relireApres(client, 'rejouerLaFile');
    };

    void rejouer();
    window.addEventListener('online', rejouer);
    return () => window.removeEventListener('online', rejouer);
  }, [client, utilisateur]);
}

/**
 * Créer ce qui attendait dans la file, en une seule demande. Une Idée (le + du téléphone,
 * chantier `telephone-projets-fiche`, ticket 05) naît En réflexion dans ce même POST (chantier
 * nettoyage-fiche, ticket 03) : elle ne peut plus rester une tâche à moitié réglée.
 */
export async function creerDepuisLaFile(titre: string, parentId: string | null, idee: boolean): Promise<void> {
  await api.creer(titre, parentId, idee ? { etat: 'sans_objet' } : {});
}

/**
 * Ce que la mise à jour optimiste pose sur la bulle : les champs demandés, et l'autre délai
 * effacé quand on en pose un, par la règle que le serveur applique aussi (`autreDelaiEfface`,
 * `commun/`). La fiche ne montre jamais les deux délais à la fois, même avant la réponse.
 */
export function champsOptimistes(champs: Partial<Bulle>): Partial<Bulle> {
  return { ...champs, ...autreDelaiEfface(champs) };
}

/**
 * Modifier une bulle. Optimiste : l'effet est immédiat, et se retire si le serveur refuse.
 *
 * Une bulle se lit sur **deux** feuilles au moins : la sienne (au centre) et celle de son
 * parent (autour). La fiche d'une sœur du centre, ouverte par un intercalaire, vit sur la
 * feuille du parent (ticket 21 de finition-v1) : ne toucher qu'à la feuille où l'on est
 * laisserait l'autre montrer l'ancienne valeur. On pose donc l'effet partout où la bulle
 * figure, et toutes les feuilles se relisent — seules celles qui sont à l'écran repartent
 * au serveur, les autres attendent qu'on y revienne.
 */
export function useModifier() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({ id, champs }: { id: string; champs: Partial<Bulle> }) =>
      api.modifier(id, champs),

    onMutate: async ({ id, champs }) => {
      await client.cancelQueries({ queryKey: PREFIXES.feuille });
      const avant = client.getQueriesData({ queryKey: PREFIXES.feuille });

      client.setQueriesData({ queryKey: PREFIXES.feuille }, (feuille: unknown) => {
        // Sous la même clé vivent aussi les cartes dépliées (`useCarteDepliee`) : des places,
        // pas des bulles, rien à y changer.
        if (!feuille || typeof feuille !== 'object' || !('enfants' in feuille)) return feuille;
        const f = feuille as { bulle: Bulle; enfants: Bulle[]; carte?: { dessous: Bulle[] } };
        const ecrits = champsOptimistes(champs);
        const change = (e: Bulle) => (e.id === id ? { ...e, ...ecrits } : e);
        return {
          ...f,
          bulle: change(f.bulle),
          enfants: f.enfants.map(change),
          // Les bulles des éventails de la carte (ticket 36) : le rond change tout de suite.
          ...(f.carte ? { carte: { ...f.carte, dessous: f.carte.dessous.map(change) } } : {}),
        };
      });

      return { avant };
    },

    onError: (_erreur, _variables, contexte) => {
      for (const [cle, donnees] of contexte?.avant ?? []) client.setQueryData(cle, donnees);
    },

    // Les feuilles, la jauge d'un projet (ticket 05 de telephone), et le jour : une échéance ou un
    // état changé change « Maintenant », l'Agenda et la journée.
    onSettled: () => void relireApres(client, 'modifier'),
  });
}

/**
 * Habiller un projet, depuis le panneau « Habit » (chantier habit-du-projet, ticket 02) : sa
 * couleur et son fond. Optimiste, comme `useModifier` : chaque feuille du projet (lui et ses
 * étapes) change tout de suite, et revient si le serveur refuse. Puis tout ce qui montre la
 * couleur d'un projet se relit, comme après une modification : la barre de gauche (`dossiers`),
 * l'accueil et la page Projets (`projets`), les feuilles, et le jour, dont les lignes disent leur
 * projet par sa couleur.
 */
export function useHabiller() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({ projetId, changement }: { projetId: string; changement: ChangementDHabit }) =>
      api.habiller(projetId, changement),

    onMutate: async ({ projetId, changement }) => {
      await client.cancelQueries({ queryKey: PREFIXES.feuille });
      const avant = client.getQueriesData({ queryKey: PREFIXES.feuille });
      client.setQueriesData({ queryKey: PREFIXES.feuille }, (feuille: Feuille | undefined) =>
        feuille ? feuilleHabillee(feuille, projetId, changement) : feuille
      );
      return { avant };
    },

    onError: (_erreur, _variables, contexte) => {
      for (const [cle, donnees] of contexte?.avant ?? []) client.setQueryData(cle, donnees);
    },

    onSettled: () => void relireApres(client, 'modifier'),
  });
}

/**
 * Changer la couleur, la forme ou le trait d'une bulle, depuis l'espace « Habit » de sa fiche ou
 * son menu « ⋯ » (chantier habit-des-bulles, modification 04). Optimiste, comme `useHabiller` : la
 * bulle change aussitôt sur la carte (`feuilleAvecLaBulleHabillee`, la bulle seule, pas tout le
 * projet), et revient si le serveur refuse ; les feuilles se relisent ensuite.
 */
export function useHabillerLaBulle() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({ id, changement }: { id: string; changement: ChangementDHabitDUneBulle }) =>
      api.habillerLaBulle(id, changement),

    onMutate: async ({ id, changement }) => {
      await client.cancelQueries({ queryKey: PREFIXES.feuille });
      const avant = client.getQueriesData({ queryKey: PREFIXES.feuille });
      client.setQueriesData({ queryKey: PREFIXES.feuille }, (feuille: Feuille | undefined) =>
        feuille ? feuilleAvecLaBulleHabillee(feuille, id, changement) : feuille
      );
      return { avant };
    },

    onError: (_erreur, _variables, contexte) => {
      for (const [cle, donnees] of contexte?.avant ?? []) client.setQueryData(cle, donnees);
    },

    onSettled: () => void relireApres(client, 'modifier'),
  });
}

/**
 * Choisir le mode d'une bulle, projet compris, depuis le panneau « Mode » (chantier
 * modes-de-la-carte, deuxième passe de 01 et 02). Optimiste, comme `useHabillerLaBulle` : les
 * touches de sa carte changent aussitôt (`feuilleAvecLeMode`), et reviennent si le serveur refuse ;
 * les feuilles se relisent ensuite, ses étapes avec le mode qu'elles prennent d'elle.
 */
export function useChoisirLeMode() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({ id, changement }: { id: string; changement: ChangementDeMode }) =>
      api.choisirLeMode(id, changement),

    onMutate: async ({ id, changement }) => {
      await client.cancelQueries({ queryKey: PREFIXES.feuille });
      const avant = client.getQueriesData({ queryKey: PREFIXES.feuille });
      client.setQueriesData({ queryKey: PREFIXES.feuille }, (feuille: Feuille | undefined) =>
        feuille ? feuilleAvecLeMode(feuille, id, changement) : feuille
      );
      return { avant };
    },

    onError: (_erreur, _variables, contexte) => {
      for (const [cle, donnees] of contexte?.avant ?? []) client.setQueryData(cle, donnees);
    },

    onSettled: () => void relireApres(client, 'modifier'),
  });
}

/**
 * Choisir le type et le papier de la fiche d'une bulle, depuis le menu « Type ▾ » (chantier
 * types-de-fiche, modification 02). Optimiste, comme `useModifier` : la bulle change partout où
 * une feuille lue la porte (au centre, parmi les filles), et revient si le serveur refuse. Les
 * cartes dépliées, rangées sous la même clé, ne portent pas de fiche : rendues telles quelles.
 */
export function useChoisirLaFiche() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({ id, changement }: { id: string; changement: ChangementDeFiche }) =>
      api.choisirLaFiche(id, changement),

    onMutate: async ({ id, changement }) => {
      await client.cancelQueries({ queryKey: PREFIXES.feuille });
      const avant = client.getQueriesData({ queryKey: PREFIXES.feuille });
      client.setQueriesData({ queryKey: PREFIXES.feuille }, (feuille: unknown) => {
        if (!feuille || typeof feuille !== 'object' || !('enfants' in feuille) || !('bulle' in feuille)) return feuille;
        const f = feuille as Feuille;
        const change = <B extends { id: string }>(b: B): B => (b.id === id ? { ...b, ...changement } : b);
        return { ...f, bulle: change(f.bulle), enfants: f.enfants.map(change) };
      });
      return { avant };
    },

    onError: (_erreur, _variables, contexte) => {
      for (const [cle, donnees] of contexte?.avant ?? []) client.setQueryData(cle, donnees);
    },

    onSettled: () => void relireApres(client, 'modifier'),
  });
}

/**
 * Répondre au monde depuis l'accueil — les deux seuls gestes de « À vérifier ».
 *
 * **« c'est arrivé »** : la réponse est là, la bulle devient acquise.
 * **« relancer »** : tu viens de relancer, donc **l'attente repart d'aujourd'hui**. C'est
 * la seule lecture honnête du mot : le délai n'a pas changé, c'est la demande qui est
 * repartie. Sans ce geste, une ligne relancée continuerait de crier tous les jours, et
 * l'app deviendrait le compteur de retard que le principe 3 interdit.
 *
 * Rien d'optimiste : les quatre voix se recalculent au serveur, et une ligne qui change
 * d'état change de voix.
 */
export function useRepondreAuMonde() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, champs }: { id: string; champs: Partial<Bulle> }) =>
      api.modifier(id, champs),
    // On relance depuis « Maintenant », la carte d'un projet (ticket 02 de telephone) ou la semaine
    // de l'Agenda (chantier `telephone-journee-agenda`, ticket 05) : tout ce qui montre la ligne.
    onSettled: () => void relireApres(client, 'repondreAuMonde'),
  });
}

/**
 * Valider une bulle, et récupérer ce qui restait dedans.
 * Pas d'optimisme ici : c'est le serveur qui dit ce que le geste laisse derrière, et cette
 * réponse-là est le sujet même du geste.
 */
export function useValider() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.valider(id),
    // Toutes les feuilles, pour la même raison que `useModifier`, et « Maintenant » : ce qu'elle
    // retenait se libère.
    onSuccess: () => void relireApres(client, 'valider'),
  });
}

/**
 * Défaire une validation, depuis l'inventaire. Pas d'optimisme non plus : tout ce qui montre la
 * bulle se relit, parce que le décompte, les dates et ce qu'elle retient reviennent à ce qu'ils
 * étaient.
 */
export function useAnnulerValidation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, etatAvant }: { id: string; etatAvant: Etat }) =>
      api.annulerValidation(id, etatAvant),
    onSettled: () => void relireApres(client, 'annulerLaValidation'),
  });
}

/**
 * Déplacer une bulle, depuis l'inventaire ou depuis la fiche. Toute la vue bouge : toutes les
 * feuilles (celle d'arrivée comme celle de départ), les dossiers du choix de la destination, et
 * « Mes projets » (chantier `deplacer`) : une bulle sortie à la racine y devient un projet, un
 * projet rangé ailleurs en disparaît, et la jauge des deux projets change.
 */
export function useDeplacer() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, parentId }: { id: string; parentId: string | null }) =>
      api.deplacer(id, parentId),
    onSuccess: () => void relireApres(client, 'deplacer'),
  });
}

/**
 * Hierarchiser. La feuille se redessine ensuite : les bulles glissent à leur place. Les autres
 * feuilles qui montrent ces sœurs (la Hiérarchie dépliée, la feuille de la mère) et « Maintenant »
 * se relisent aussi.
 */
export function usePoserRangs() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (rangs: { id: string; rang: number | null }[]) => api.poserRangs(rangs),
    onSuccess: () => void relireApres(client, 'ranger'),
  });
}

/**
 * Supprimer un dossier, avec tout ce qu'il contient.
 *
 * Pas d'optimisme : on ne retire la carte qu'une fois le serveur d'accord, parce qu'une
 * suppression qui réapparaît ferait douter de toutes les autres. Ensuite on la retire
 * tout de suite de « Mes dossiers », sans attendre le rechargement, puis on relit tout :
 * le vivier, l'agenda, la capture et « Déplacer » connaissaient ce dossier.
 */
export function useSupprimerDossier() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.supprimer(id),
    onSuccess: (_rien, id) => {
      client.setQueryData(cles.projets, (avant: { dossiers: DossierVu[] } | undefined) =>
        avant ? { ...avant, dossiers: avant.dossiers.filter((d) => d.id !== id) } : avant
      );
      client.removeQueries({ queryKey: cles.feuille(id) });
      void relireApres(client, 'supprimer');
    },
  });
}

/**
 * Ce que supprimer une bulle emporterait, lu à l'ouverture de la confirmation et jamais
 * gardé : le nombre doit être celui du moment où l'on confirme.
 */
export function useEmportees(id: string) {
  return useQuery({
    queryKey: cles.emportees(id),
    queryFn: () => api.emportees(id),
    staleTime: 0,
    gcTime: 0,
  });
}

/**
 * Supprimer une bulle depuis sa fiche, avec tout ce qu'elle contient (ticket 10 de
 * retours-testeurs). Pas d'optimisme, pour la même raison que `useSupprimerDossier` ; sa
 * feuille part du cache, puis tout se relit : les conditions qui l'attendaient sont tombées
 * avec elle côté serveur, et ce qu'elles retenaient ne l'est plus.
 */
export function useSupprimerBulle() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.supprimer(id),
    onSuccess: (_rien, id) => {
      client.removeQueries({ queryKey: cles.feuille(id) });
      client.removeQueries({ queryKey: cles.emportees(id) });
      void relireApres(client, 'supprimer');
    },
  });
}

/**
 * Se deconnecter.
 *
 * Deux gestes, pas un : on ferme la session cote serveur, ET on vide la file de capture
 * locale. Les titres captures hors ligne dorment en clair dans IndexedDB — les laisser
 * apres un « se deconnecter » reviendrait a montrer a la personne suivante ce a quoi on
 * pensait dans le bus.
 *
 * Ce qui n'etait pas encore parti est perdu, et c'est assume : c'est le sens de la
 * deconnexion. Le vider est un choix explicite, pas un oubli.
 */
export function useDeconnexion() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      // Avant la session : un appareil d'où l'on sort ne reçoit plus les notifications du compte
      // (chantier notifications-socle). Sans abonnement, rien ; un échec n'empêche pas de sortir.
      await desabonnerAvantDeSortir();
      await api.deconnexion();
      await viderLaFile();
    },
    onSuccess: () => {
      // Tout ce qui a été lu part du cache, SAUF la requête de session, posée à « personne » :
      // la retirer aussi (`client.clear()`) laissait les écrans qui l'observent sur l'ancienne
      // réponse, et l'app restait ouverte après « sortir » (vu au ticket 02 du chantier comptes).
      client.removeQueries({ predicate: (requete) => requete.queryKey[0] !== cles.session[0] });
      client.setQueryData<ReponseSession>(cles.session, { connecte: false, utilisateur: null, email: null });
      // Après « Sortir », la connexion (`/connexion`), pas la vitrine ; le prochain qui entre part
      // de là, puis de l'accueil (`App.tsx`) : pas de l'écran où l'autre était.
      window.history.replaceState(null, '', '/connexion');
    },
  });
}
